<!-- 平台管理端 — 商家入驻审核详情 -->
<template>
  <!-- 基本信息 -->
  <ContentWrap title="申请信息">
    <template #header>
      <div class="flex flex-grow justify-end">
        <el-button link type="primary" @click="backToList">
          返回列表
        </el-button>
      </div>
    </template>
    <el-descriptions :column="2" border>
      <el-descriptions-item label="公司名称" min-width="150">{{ detail.companyName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="店铺名称">{{ detail.shopName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="商家类型">{{ merchantTypeLabel }}</el-descriptions-item>
      <el-descriptions-item label="入驻套餐">
        <span v-if="detail.planId">
          ID {{ detail.planId }} · ¥{{ ((detail.planPrice || 0) / 100).toFixed(2) }} ·
          上限 {{ detail.planSpuLimit === -1 || detail.planSpuLimit == null ? '不限' : detail.planSpuLimit }}
        </span>
        <span v-else>-</span>
      </el-descriptions-item>
      <el-descriptions-item label="支付状态">
        <el-tag size="small">{{ payStatusLabel(detail.payStatus) }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="法人姓名">{{ detail.legalPerson || '-' }}</el-descriptions-item>
      <el-descriptions-item label="联系电话">{{ detail.contactPhone || '-' }}</el-descriptions-item>
      <el-descriptions-item label="联系邮箱">{{ detail.contactEmail || '-' }}</el-descriptions-item>
      <el-descriptions-item label="统一社会信用代码">{{ detail.creditCode || '-' }}</el-descriptions-item>
      <el-descriptions-item label="法人身份证号">{{ detail.idCardNo || '-' }}</el-descriptions-item>
      <el-descriptions-item label="店铺地址" :span="2">{{ detail.address || '-' }}</el-descriptions-item>
      <el-descriptions-item label="营业时间">{{ detail.businessHours || '-' }}</el-descriptions-item>
      <el-descriptions-item label="人均消费">{{ detail.avgPrice != null ? '¥' + detail.avgPrice : '-' }}</el-descriptions-item>
      <el-descriptions-item label="审核状态">
        <el-tag :type="statusTagType(detail.auditStatus)">{{ statusLabel(detail.auditStatus) }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="店铺简介" :span="2">{{ detail.shopDesc || '-' }}</el-descriptions-item>
      <el-descriptions-item label="申请时间">{{ formatDate(detail.createTime) || '-' }}</el-descriptions-item>
      <el-descriptions-item v-if="detail.auditTime" label="审核时间">{{ formatDate(detail.auditTime) }}</el-descriptions-item>
    </el-descriptions>
  </ContentWrap>

  <!-- 证件信息 -->
  <ContentWrap title="证件资料" class="mt-16px">
    <el-row :gutter="20">
      <el-col :span="8">
        <div class="cert-item">
          <div class="cert-label">店铺 Logo</div>
          <el-image v-if="detail.shopLogo" :src="detail.shopLogo" fit="contain" class="cert-img" :preview-src-list="[detail.shopLogo]" />
          <span v-else class="text-gray-400">未上传</span>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="cert-item">
          <div class="cert-label">营业执照</div>
          <el-image v-if="detail.businessLicense" :src="detail.businessLicense" fit="contain" class="cert-img" :preview-src-list="[detail.businessLicense]" />
          <span v-else class="text-gray-400">未上传</span>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="cert-item">
          <div class="cert-label">身份证正面</div>
          <el-image v-if="detail.idCardFront" :src="detail.idCardFront" fit="contain" class="cert-img" :preview-src-list="[detail.idCardFront]" />
          <span v-else class="text-gray-400">未上传</span>
        </div>
      </el-col>
      <el-col :span="8">
        <div class="cert-item">
          <div class="cert-label">身份证背面</div>
          <el-image v-if="detail.idCardBack" :src="detail.idCardBack" fit="contain" class="cert-img" :preview-src-list="[detail.idCardBack]" />
          <span v-else class="text-gray-400">未上传</span>
        </div>
      </el-col>
      <el-col :span="16">
        <div class="cert-item">
          <div class="cert-label">特殊资质文件</div>
          <div v-if="qualificationUrls.length" class="qual-grid">
            <el-image
              v-for="(url, idx) in qualificationUrls"
              :key="idx"
              :src="url"
              fit="contain"
              class="qual-img"
              :preview-src-list="qualificationUrls"
              :initial-index="idx"
              preview-teleported
            />
          </div>
          <span v-else class="text-gray-400">未上传</span>
        </div>
      </el-col>
    </el-row>
  </ContentWrap>

  <!-- 结算信息 -->
  <ContentWrap title="结算信息" class="mt-16px">
    <el-descriptions :column="2" border>
      <el-descriptions-item label="开户名称">{{ detail.bankName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="开户银行">{{ detail.bankType || '-' }}</el-descriptions-item>
      <el-descriptions-item label="银行账号">{{ detail.bankAccount || '-' }}</el-descriptions-item>
      <el-descriptions-item label="开户支行">{{ detail.bankBranch || '-' }}</el-descriptions-item>
    </el-descriptions>
  </ContentWrap>

  <!-- 登录信息 -->
  <ContentWrap title="登录信息" class="mt-16px">
    <el-descriptions :column="2" border>
      <el-descriptions-item label="登录账号">{{ detail.loginUsername || '-' }}</el-descriptions-item>
      <el-descriptions-item label="账号状态">
        <el-tag v-if="detail.auditStatus === 1" type="success">已开通（审核通过后创建）</el-tag>
        <el-tag v-else type="info">审核通过后开通</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="经营类目">
        <template v-if="categoryLabels.length">
          <el-tag
            v-for="item in categoryLabels"
            :key="item.id"
            class="mr-8px mb-4px"
            type="info"
          >
            {{ item.name }}
          </el-tag>
        </template>
        <span v-else>-</span>
      </el-descriptions-item>
    </el-descriptions>
    <el-button
      v-if="detail.auditStatus === 1 && detail.loginUsername"
      type="warning"
      plain
      class="mt-16px"
      :loading="resetting"
      @click="handleResetPassword"
    >
      <Icon icon="ep:key" class="mr-5px" />重置商家登录密码
    </el-button>
  </ContentWrap>

  <!-- 历史审核记录（拒绝时显示原因） -->
  <ContentWrap v-if="detail.auditStatus === 2 && detail.auditRemark" title="审核反馈" class="mt-16px">
    <el-alert type="error" :closable="false" show-icon>
      <template #title>
        <span class="font-bold">审核拒绝原因</span>
      </template>
      {{ detail.auditRemark }}
    </el-alert>
  </ContentWrap>

  <!-- 审核操作（仅待审核状态显示） -->
  <ContentWrap v-if="detail.auditStatus === 0" title="审核操作" class="mt-16px">
    <el-form ref="auditFormRef" :model="auditForm" label-width="100px">
      <el-form-item label="类目佣金率">
        <el-table v-if="commissionRows.length" :data="commissionRows" border size="small" class="w-full">
          <el-table-column label="类目名称" min-width="180" prop="name" show-overflow-tooltip />
          <el-table-column label="平台默认佣金率" width="140" align="center">
            <template #default="{ row }">
              {{ row.defaultCommissionRate != null ? row.defaultCommissionRate + '%' : '-' }}
            </template>
          </el-table-column>
          <el-table-column label="本次佣金率(%)" width="180" align="center">
            <template #default="{ row }">
              <el-input-number
                v-model="row.commissionRate"
                :min="0"
                :max="100"
                :precision="2"
                :step="0.5"
                controls-position="right"
                class="!w-140px"
              />
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else description="该申请未选择经营类目，可直接通过（不配置佣金）" :image-size="64" />
        <div class="form-tip">选择经营类目时默认带出平台类目佣金率，可通过审核前按需调整；拒绝时无需填写</div>
      </el-form-item>
      <el-form-item label="审核备注" prop="auditRemark">
        <el-input
          v-model="auditForm.auditRemark"
          type="textarea"
          :rows="3"
          placeholder="审核意见（拒绝时必填）"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>
      <el-form-item>
        <el-button type="success" :loading="submitting" @click="handleAudit(true)">
          <Icon icon="ep:check" class="mr-5px" />通过
        </el-button>
        <el-button type="danger" :loading="submitting" @click="handleAudit(false)">
          <Icon icon="ep:close" class="mr-5px" />拒绝
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getApplyDetail, auditApply, resetApplyPassword } from '@/api/merchant/apply';
import * as ProductCategoryApi from '@/api/mall/product/category';
import type { CategoryVO } from '@/api/mall/product/category';
import { formatDate } from '@/utils/formatTime';
import { ContentWrap } from '@/components/ContentWrap';
import { useMallStandaloneNav } from '@/hooks/web/useMallStandaloneNav';
import { useMallSelfPlatformOnlyGuard } from '@/hooks/web/useMallSelfPlatformOnlyGuard';
import { DICT_TYPE, getDictLabel } from '@/utils/dict';

defineOptions({ name: 'MerchantApplyDetail' });

interface CommissionRow {
  id: number;
  name: string;
  defaultCommissionRate: number | null;
  commissionRate: number | undefined;
}

const route = useRoute();
const { pushMall } = useMallStandaloneNav();
const { guardPlatformOnlyPage } = useMallSelfPlatformOnlyGuard();
function backToList() {
  pushMall('/merchant/shop-manager/merchant-apply');
}
const submitting = ref(false);
const resetting = ref(false);
const detail = reactive<any>({});
const categoryMap = ref<Map<number, CategoryVO>>(new Map());
const commissionRows = ref<CommissionRow[]>([]);
const auditForm = reactive({
  auditRemark: '',
});

const categoryIds = computed(() => parseCategoryIds(detail.businessCategoryIds));

/** 商家类型：取自数据字典 merchant_type（系统管理 → 字典管理 可维护） */
const merchantTypeLabel = computed(
  () => getDictLabel(DICT_TYPE.MERCHANT_TYPE, detail.merchantType) || '其他'
);

const qualificationUrls = computed(() => {
  const raw = detail.qualificationUrls;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  try {
    const arr = JSON.parse(String(raw));
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch {
    return [];
  }
});

const categoryLabels = computed(() => {
  return categoryIds.value.map((id) => {
    const cat = categoryMap.value.get(id);
    return { id, name: cat?.name || `类目#${id}` };
  });
});

function statusLabel(status: number) {
  const map: Record<number, string> = { 0: '待审核', 1: '已通过', 2: '已拒绝', 3: '已撤销' };
  return map[status] ?? '未知';
}

function statusTagType(status: number) {
  const map: Record<number, string> = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'info' };
  return map[status] ?? 'info';
}

function payStatusLabel(status?: number) {
  const map: Record<number, string> = { 0: '无需付', 1: '待支付', 2: '已支付' };
  return map[status ?? 0] ?? '-';
}

/** 兼容 JSON 数组字符串与历史逗号串 */
function parseCategoryIds(raw: unknown): number[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => Number.isFinite(n));
  }
  const s = String(raw).trim();
  if (!s) return [];
  try {
    if (s.startsWith('[')) {
      const arr = JSON.parse(s);
      if (Array.isArray(arr)) {
        return arr.map(Number).filter((n) => Number.isFinite(n));
      }
    }
  } catch {
    // fall through
  }
  return s
    .split(',')
    .map((x) => Number(x.trim()))
    .filter((n) => Number.isFinite(n));
}

function rebuildCommissionRows() {
  commissionRows.value = categoryIds.value.map((id) => {
    const cat = categoryMap.value.get(id);
    const defaultRate =
      cat?.defaultCommissionRate != null ? Number(cat.defaultCommissionRate) : null;
    return {
      id,
      name: cat?.name || `类目#${id}`,
      defaultCommissionRate: defaultRate,
      commissionRate: defaultRate != null ? defaultRate : undefined,
    };
  });
}

function buildCommissionRatesJson(): string | undefined {
  if (!commissionRows.value.length) return undefined;
  const obj: Record<string, number> = {};
  for (const row of commissionRows.value) {
    if (row.commissionRate == null || Number.isNaN(Number(row.commissionRate))) {
      continue;
    }
    obj[String(row.id)] = Number(row.commissionRate);
  }
  return Object.keys(obj).length ? JSON.stringify(obj) : undefined;
}

function validateCommissionForApprove(): boolean {
  if (!commissionRows.value.length) {
    // 移动端/管理端未选择经营类目时允许直接通过，佣金配置留空
    return true;
  }
  for (const row of commissionRows.value) {
    if (row.commissionRate == null || Number.isNaN(Number(row.commissionRate))) {
      ElMessage.warning(`请填写类目「${row.name}」的佣金率`);
      return false;
    }
    const rate = Number(row.commissionRate);
    if (rate < 0 || rate > 100) {
      ElMessage.warning(`类目「${row.name}」佣金率须在 0–100 之间`);
      return false;
    }
  }
  return true;
}

async function loadCategories() {
  const list = (await ProductCategoryApi.getCategoryList({})) as CategoryVO[];
  const map = new Map<number, CategoryVO>();
  for (const item of list || []) {
    if (item.id != null) {
      map.set(item.id, item);
    }
  }
  categoryMap.value = map;
}

async function fetchDetail() {
  const id = Number(route.params.id);
  if (!id) return;
  const data = await getApplyDetail(id);
  Object.assign(detail, data || {});
  rebuildCommissionRows();
}

async function handleAudit(approved: boolean) {
  if (!approved && !auditForm.auditRemark.trim()) {
    ElMessage.warning('拒绝时必须填写审核备注');
    return;
  }
  if (approved && !validateCommissionForApprove()) {
    return;
  }
  const action = approved ? '通过' : '拒绝';
  try {
    await ElMessageBox.confirm(`确认${action}该入驻申请？`, '审核确认', { type: 'warning' });
    submitting.value = true;
    await auditApply({
      id: detail.id,
      approved,
      auditRemark: auditForm.auditRemark,
      // 页面表格组装 JSON；运营不手写。拒绝时可不传
      categoryCommissionRates: approved ? buildCommissionRatesJson() : undefined,
    });
    ElMessage.success(`已${action}`);
    backToList();
  } catch (e: any) {
    if (e !== 'cancel') {
      // error handled by interceptor
    }
  } finally {
    submitting.value = false;
  }
}

async function handleResetPassword() {
  const { value } = await ElMessageBox.prompt(
    '请输入新的登录密码（至少 6 位），重置后请及时告知商家。',
    '重置商家登录密码',
    {
      inputType: 'password',
      inputPattern: /^.{6,100}$/,
      inputErrorMessage: '密码长度不能少于 6 位',
      confirmButtonText: '确认重置',
      cancelButtonText: '取消',
    }
  ).catch(() => ({ value: undefined }));
  if (!value) return;
  resetting.value = true;
  try {
    await resetApplyPassword(detail.id, value);
    ElMessage.success('密码已重置，请将新密码告知商家');
  } catch (e: any) {
    if (e !== 'cancel') {
      // 错误已由拦截器统一提示
    }
  } finally {
    resetting.value = false;
  }
}

onMounted(async () => {
  if (guardPlatformOnlyPage()) {
    return;
  }
  await loadCategories();
  await fetchDetail();
});
</script>

<style lang="scss" scoped>
.cert-item {
  .cert-label {
    font-size: 14px;
    color: var(--el-text-color-secondary);
    margin-bottom: 10px;
  }
  .cert-img {
    width: 100%;
    height: 200px;
    border-radius: 6px;
    border: 1px solid var(--el-border-color-light);
  }
}
.qual-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.qual-img {
  width: 160px;
  height: 120px;
  border-radius: 6px;
  border: 1px solid var(--el-border-color-light);
}
.form-tip {
  font-size: 12px;
  color: var(--el-text-color-placeholder);
  margin-top: 4px;
}
</style>
